import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import * as companyApi from '../services/company-api.js';
import type { Company, CompanyAgent, AgentThread, AgentThreadMessage } from '../services/company-api.js';
import { wsClient } from '../services/websocket-client.js';
import '../components/shared/cp-page-subnav.js';

const COMM_TABS = [
  { label: 'CEO Chats', path: '/ceo-chats' },
  { label: 'Nachrichten', path: '/messages' },
  { label: 'Inbox', path: '/inbox' },
  { label: 'Kontakte', path: '/people' },
];

const LAST_THREAD_KEY = 'ceo-chats:lastThreadId';
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_IMAGES = 5;

interface CompanyChatGroup {
  company: Company;
  ceoAgent: CompanyAgent | null;
  threads: AgentThread[];
}

@customElement('cp-page-ceo-chats')
export class CpPageCeoChats extends LitElement {
  createRenderRoot() { return this; }

  @state() private _groups: CompanyChatGroup[] = [];
  @state() private _loadingSidebar = false;
  @state() private _activeThread: AgentThread | null = null;
  @state() private _activeCompanyName = '';
  @state() private _messages: AgentThreadMessage[] = [];
  @state() private _inputValue = '';
  @state() private _sending = false;
  @state() private _loadingThread = false;
  @state() private _error = '';
  @state() private _newThreadCompanyId: number | null = null;
  @state() private _newTopicValue = '';
  @state() private _editingMessageId: number | null = null;
  @state() private _editingContent = '';
  @state() private _mobileShowChat = false;
  @state() private _composerExpanded = false;
  @state() private _pendingImages: string[] = [];
  @state() private _uploading = false;
  @state() private _dragOver = false;

  private _wsHandler = ((e: CustomEvent) => {
    const msg = e.detail as { type: string; payload: { threadId: number; message: AgentThreadMessage } };

    if (msg.type !== 'agent-thread:response') return;

    const { threadId, message } = msg.payload;
    if (this._activeThread?.id === threadId) {
      this._messages = [...this._messages, message];
      this._sending = false;
      this._scrollToBottom();
      // Update thread timestamp in sidebar
      this._groups = this._groups.map(g => ({
        ...g,
        threads: g.threads.map(t =>
          t.id === threadId ? { ...t, updated_at: message.created_at } : t
        ),
      }));
    }
  }) as EventListener;

  override connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler);
    void this._loadAll();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler);
  }

  /** Stop the running reply and take the message back into the composer (INT-2026-004). */
  private async _stop(): Promise<void> {
    if (!this._activeThread) return;
    const threadId = this._activeThread.id;
    try {
      const cancelled = await companyApi.cancelAgentThreadMessage(threadId);
      if (cancelled && this._activeThread?.id === threadId) {
        const lastUser = [...this._messages].reverse().find(m => m.role === 'user');
        if (lastUser) this._messages = this._messages.filter(m => m.id < lastUser.id);
        if (!this._inputValue.trim()) this._inputValue = cancelled.content === '(Bild angehaengt)' ? '' : cancelled.content;
        this._pendingImages = [...new Set([...this._pendingImages, ...cancelled.attachments])];
        void this.updateComplete.then(() => this._autoGrow());
      } else if (!cancelled && this._activeThread?.id === threadId) {
        // 409: reply just landed or the server restarted — show the real state instead of guessing.
        this._messages = (await companyApi.getAgentThread(threadId)).messages;
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Stoppen';
    } finally {
      // Also covers 409 (reply just arrived or server restarted): leave the waiting state either way.
      this._sending = false;
    }
  }

  private _autoGrow(): void {
    const el = this.querySelector<HTMLTextAreaElement>('.cp-page-ceo-chats__input');
    if (!el) return;
    el.style.height = 'auto';
    // Expanded mode sizes the textarea via CSS (flex); auto-grow only in the compact composer.
    if (!this._composerExpanded) el.style.height = `${el.scrollHeight + 2}px`;
  }

  private _toggleComposer(): void {
    this._composerExpanded = !this._composerExpanded;
    void this.updateComplete.then(() => {
      this._autoGrow();
      this.querySelector<HTMLTextAreaElement>('.cp-page-ceo-chats__input')?.focus();
    });
  }

  private async _addImages(files: File[]): Promise<void> {
    if (!this._activeThread) return;
    const images = files.filter(f => IMAGE_TYPES.includes(f.type));
    if (images.length < files.length) this._error = 'Nur Bilder (PNG, JPEG, WebP, GIF) werden angehaengt.';
    if (images.length === 0) return;
    const threadId = this._activeThread.id;
    this._uploading = true;
    try {
      for (const file of images) {
        if (this._pendingImages.length >= MAX_IMAGES) {
          this._error = `Hoechstens ${MAX_IMAGES} Bilder pro Nachricht.`;
          break;
        }
        const { name } = await companyApi.uploadAgentThreadAttachment(threadId, file);
        if (this._activeThread?.id === threadId) this._pendingImages = [...this._pendingImages, name];
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Hochladen';
    } finally {
      this._uploading = false;
    }
  }

  private _onPaste(e: ClipboardEvent): void {
    const files = Array.from(e.clipboardData?.files ?? []);
    // Office/Excel put a picture of copied text on the clipboard too; text wins.
    if (files.length === 0 || e.clipboardData?.types.includes('text/plain')) return;
    e.preventDefault();
    void this._addImages(files);
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    this._dragOver = false;
    void this._addImages(Array.from(e.dataTransfer?.files ?? []));
  }

  private _pickImages(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = IMAGE_TYPES.join(',');
    input.addEventListener('change', () => void this._addImages(Array.from(input.files ?? [])));
    input.click();
  }

  private async _loadAll(): Promise<void> {
    this._loadingSidebar = true;
    try {
      const companies = await companyApi.listCompanies('active');
      const groups: CompanyChatGroup[] = [];

      // Load agents + threads for each company in parallel
      const results = await Promise.all(
        companies.map(async (company) => {
          const [agents, threads] = await Promise.all([
            companyApi.listCompanyAgents(company.id),
            companyApi.listAgentThreads(company.id, 'founder-to-agent'),
          ]);
          const ceoAgent = agents.find(a => a.role === 'ceo') ?? null;
          return { company, ceoAgent, threads };
        })
      );

      // Only show companies that have a CEO agent
      for (const r of results) {
        if (r.ceoAgent) {
          groups.push(r);
        }
      }

      this._groups = groups;

      // Restore last selected thread from localStorage
      const lastThreadId = localStorage.getItem(LAST_THREAD_KEY);
      if (lastThreadId) {
        const id = Number(lastThreadId);
        for (const group of groups) {
          const thread = group.threads.find(t => t.id === id && t.status === 'active');
          if (thread) {
            void this._selectThread(thread, group.company.name);
            break;
          }
        }
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden';
    } finally {
      this._loadingSidebar = false;
    }
  }

  private async _selectThread(thread: AgentThread, companyName: string): Promise<void> {
    if (this._loadingThread) return;
    this._loadingThread = true;
    this._activeThread = thread;
    this._activeCompanyName = companyName;
    this._mobileShowChat = true;
    localStorage.setItem(LAST_THREAD_KEY, String(thread.id));
    this._messages = [];
    this._editingMessageId = null;
    this._editingContent = '';
    this._pendingImages = [];
    this._composerExpanded = false;
    try {
      const [data, pendingStatus] = await Promise.all([
        companyApi.getAgentThread(thread.id),
        companyApi.getThreadPendingStatus(thread.id),
      ]);
      this._messages = data.messages;
      this._sending = pendingStatus.pending;
      this._scrollToBottom();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden des Threads';
    } finally {
      this._loadingThread = false;
    }
  }

  private _startNewThread(companyId: number, e: Event): void {
    e.stopPropagation();
    this._newThreadCompanyId = this._newThreadCompanyId === companyId ? null : companyId;
    this._newTopicValue = '';
  }

  private async _createThread(): Promise<void> {
    const topic = this._newTopicValue.trim();
    if (!topic || !this._newThreadCompanyId) return;

    const group = this._groups.find(g => g.company.id === this._newThreadCompanyId);
    if (!group?.ceoAgent) return;

    try {
      const thread = await companyApi.createAgentThread({
        company_id: this._newThreadCompanyId,
        agent_id: group.ceoAgent.id,
        topic,
      });
      // Add to sidebar
      this._groups = this._groups.map(g =>
        g.company.id === this._newThreadCompanyId
          ? { ...g, threads: [thread, ...g.threads] }
          : g
      );
      this._newThreadCompanyId = null;
      this._newTopicValue = '';
      await this._selectThread(thread, group.company.name);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen des Threads';
    }
  }

  private async _deleteThread(thread: AgentThread, e: Event): Promise<void> {
    e.stopPropagation();
    if (!confirm(`Thread "${thread.topic}" wirklich loeschen?`)) return;

    try {
      await companyApi.deleteAgentThread(thread.id);
      this._groups = this._groups.map(g => ({
        ...g,
        threads: g.threads.filter(t => t.id !== thread.id),
      }));
      if (this._activeThread?.id === thread.id) {
        this._activeThread = null;
        this._messages = [];
        localStorage.removeItem(LAST_THREAD_KEY);
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Loeschen';
    }
  }

  private _startEdit(msg: AgentThreadMessage): void {
    if (this._sending) return;
    this._editingMessageId = msg.id;
    this._editingContent = msg.content;
  }

  private _cancelEdit(): void {
    this._editingMessageId = null;
    this._editingContent = '';
  }

  private _onEditKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this._cancelEdit();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this._submitEdit();
    }
  }

  private async _submitEdit(): Promise<void> {
    const content = this._editingContent.trim();
    if (!content || !this._activeThread || !this._editingMessageId || this._sending) return;

    const messageId = this._editingMessageId;
    const threadId = this._activeThread.id;

    // Optimistic: truncate messages from edit point
    const preservedMessages = this._messages.filter(m => m.id < messageId);
    this._messages = preservedMessages;
    this._editingMessageId = null;
    this._editingContent = '';
    this._sending = true;
    this._error = '';

    try {
      const result = await companyApi.editAndResendAgentThreadMessage(threadId, messageId, content);
      this._messages = [...preservedMessages, result.userMessage];
      this._scrollToBottom();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Bearbeiten';
      this._sending = false;
      try {
        const data = await companyApi.getAgentThread(threadId);
        this._messages = data.messages;
      } catch {
        // keep error visible
      }
    }
  }

  private async _sendMessage(): Promise<void> {
    const content = this._inputValue.trim();
    const images = this._pendingImages;
    if ((!content && images.length === 0) || !this._activeThread || this._sending || this._uploading) return;

    this._inputValue = '';
    this._pendingImages = [];
    this._composerExpanded = false;
    this._sending = true;
    this._error = '';
    void this.updateComplete.then(() => this._autoGrow());

    try {
      const result = await companyApi.sendAgentThreadMessage(this._activeThread.id, content, images);
      this._messages = [...this._messages, result.userMessage];
      this._scrollToBottom();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Senden';
      this._inputValue = content;
      this._pendingImages = images;
      this._sending = false;
    }
  }

  private _scrollToBottom(): void {
    void this.updateComplete.then(() => {
      const container = this.querySelector('.cp-page-ceo-chats__messages');
      if (container) container.scrollTop = container.scrollHeight;
    });
  }

  private _onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this._composerExpanded) {
      e.preventDefault();
      this._toggleComposer();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this._sendMessage();
    }
  }

  private _onTopicKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void this._createThread();
    } else if (e.key === 'Escape') {
      this._newThreadCompanyId = null;
    }
  }

  private _formatTime(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  private _renderMarkdown(content: string): ReturnType<typeof html> {
    const rawHtml = marked.parse(content, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private _renderSidebar() {
    if (this._loadingSidebar) {
      return html`<div class="cp-page-ceo-chats__sidebar-loading">Laden...</div>`;
    }

    if (this._groups.length === 0) {
      return html`<div class="cp-page-ceo-chats__sidebar-empty">Keine Firmen mit CEO gefunden.</div>`;
    }

    return html`
      ${this._groups.map(group => this._renderCompanyGroup(group))}
    `;
  }

  private _renderCompanyGroup(group: CompanyChatGroup) {
    const { company, threads } = group;
    const activeThreads = threads.filter(t => t.status === 'active');
    const isNewThread = this._newThreadCompanyId === company.id;

    return html`
      <div class="cp-page-ceo-chats__company-group">
        <div class="cp-page-ceo-chats__company-header">
          <span
            class="cp-page-ceo-chats__company-dot"
            style="background-color: ${company.color ?? '#6B7280'}"
          ></span>
          <span class="cp-page-ceo-chats__company-name">${company.name}</span>
          ${activeThreads.length > 0 ? html`
            <span class="cp-page-ceo-chats__company-count">${activeThreads.length}</span>
          ` : nothing}
          <button
            class="cp-page-ceo-chats__new-btn"
            @click=${(e: Event) => this._startNewThread(company.id, e)}
            title="Neuen Thread starten"
          >+</button>
        </div>

        ${isNewThread ? html`
          <div class="cp-page-ceo-chats__new-thread">
            <input
              class="cp-page-ceo-chats__topic-input"
              type="text"
              placeholder="Thema..."
              .value=${this._newTopicValue}
              @input=${(e: Event) => { this._newTopicValue = (e.target as HTMLInputElement).value; }}
              @keydown=${this._onTopicKeydown}
              autofocus
            />
            <button
              class="cp-page-ceo-chats__topic-confirm"
              @click=${() => void this._createThread()}
              ?disabled=${!this._newTopicValue.trim()}
            >OK</button>
          </div>
        ` : nothing}

        <ul class="cp-page-ceo-chats__thread-list">
          ${activeThreads.map(t => this._renderThreadItem(t, company.name))}
          ${activeThreads.length === 0 && !isNewThread ? html`
            <li class="cp-page-ceo-chats__no-threads">Keine Threads</li>
          ` : nothing}
        </ul>
      </div>
    `;
  }

  private _renderThreadItem(thread: AgentThread, companyName: string) {
    const isActive = this._activeThread?.id === thread.id;
    return html`
      <li
        class="cp-page-ceo-chats__thread-item${isActive ? ' cp-page-ceo-chats__thread-item--active' : ''}"
        @click=${() => void this._selectThread(thread, companyName)}
      >
        <span class="cp-page-ceo-chats__thread-topic">${thread.topic}</span>
        <span class="cp-page-ceo-chats__thread-date">${this._formatTime(thread.updated_at)}</span>
        <button
          class="cp-page-ceo-chats__thread-delete"
          @click=${(e: Event) => void this._deleteThread(thread, e)}
          title="Thread loeschen"
        >&times;</button>
      </li>
    `;
  }

  private _renderMessage(msg: AgentThreadMessage) {
    const isUser = msg.role === 'user';
    const isEditing = this._editingMessageId === msg.id;

    if (isUser && isEditing) {
      return html`
        <div class="cp-company-chat__message cp-company-chat__message--user">
          <div class="cp-company-chat__message-role">Du</div>
          <div class="cp-company-chat__message-bubble cp-company-chat__message-bubble--editing">
            <textarea
              class="cp-company-chat__edit-textarea"
              rows="3"
              .value=${this._editingContent}
              @input=${(e: Event) => { this._editingContent = (e.target as HTMLTextAreaElement).value; }}
              @keydown=${this._onEditKeydown}
            ></textarea>
            <div class="cp-company-chat__edit-actions">
              <button class="cp-company-chat__edit-cancel" @click=${this._cancelEdit}>Abbrechen</button>
              <button
                class="cp-company-chat__edit-submit"
                @click=${() => void this._submitEdit()}
                ?disabled=${!this._editingContent.trim()}
              >Senden</button>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="cp-company-chat__message cp-company-chat__message--${msg.role}">
        <div class="cp-company-chat__message-role">${isUser ? 'Du' : 'CEO'}</div>
        <div class="cp-company-chat__message-bubble">
          <div class="cp-company-chat__message-content${isUser ? '' : ' cp-company-chat__message-content--markdown'}">
            ${isUser ? msg.content : this._renderMarkdown(msg.content)}
          </div>
          ${this._renderImages(msg)}
          <div class="cp-company-chat__message-time">
            ${this._formatTime(msg.created_at)}
            ${isUser ? html`
              <button
                class="cp-company-chat__edit-btn"
                @click=${() => this._startEdit(msg)}
                title="Nachricht bearbeiten"
              >&#9998;</button>
            ` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private _renderImages(msg: AgentThreadMessage) {
    const names = companyApi.parseAgentThreadAttachments(msg.attachments);
    if (names.length === 0) return nothing;
    return html`
      <div class="cp-page-ceo-chats__message-images">
        ${names.map(name => {
          const url = companyApi.agentThreadAttachmentUrl(msg.thread_id, name);
          return html`<a href=${url} target="_blank" rel="noopener"><img class="cp-page-ceo-chats__thumb" src=${url} alt="Angehaengtes Bild" loading="lazy" /></a>`;
        })}
      </div>
    `;
  }

  private _renderComposer(threadId: number) {
    const canSend = (this._inputValue.trim() || this._pendingImages.length > 0) && !this._uploading;
    return html`
      <div
        class="cp-page-ceo-chats__composer${this._dragOver ? ' cp-page-ceo-chats__composer--dragover' : ''}"
        @dragover=${(e: DragEvent) => { e.preventDefault(); this._dragOver = true; }}
        @dragleave=${() => { this._dragOver = false; }}
        @drop=${this._onDrop}
      >
        ${this._pendingImages.length > 0 || this._uploading ? html`
          <div class="cp-page-ceo-chats__pending">
            ${this._pendingImages.map(name => html`
              <div class="cp-page-ceo-chats__pending-item">
                <img class="cp-page-ceo-chats__thumb" src=${companyApi.agentThreadAttachmentUrl(threadId, name)} alt="Bild-Vorschau" />
                <button
                  class="cp-page-ceo-chats__pending-remove"
                  @click=${() => { this._pendingImages = this._pendingImages.filter(n => n !== name); }}
                  title="Bild entfernen"
                  aria-label="Bild entfernen"
                >&times;</button>
              </div>
            `)}
            ${this._uploading ? html`<span>Hochladen...</span>` : nothing}
          </div>
        ` : nothing}
        <div class="cp-page-ceo-chats__input-row">
          <button
            class="cp-page-ceo-chats__icon-btn"
            @click=${this._pickImages}
            ?disabled=${this._sending || this._uploading}
            title="Bild anhaengen"
            aria-label="Bild anhaengen"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <textarea
            class="cp-page-ceo-chats__input"
            rows="3"
            placeholder="Nachricht an CEO... (Bilder einfuegen mit Cmd+V)"
            .value=${this._inputValue}
            @input=${(e: Event) => { this._inputValue = (e.target as HTMLTextAreaElement).value; this._autoGrow(); }}
            @keydown=${this._onKeydown}
            @paste=${this._onPaste}
            ?disabled=${this._sending}
          ></textarea>
          <div class="cp-page-ceo-chats__actions">
            <button
              class="cp-page-ceo-chats__icon-btn"
              @click=${this._toggleComposer}
              title=${this._composerExpanded ? 'Eingabe verkleinern (Esc)' : 'Eingabe vergroessern'}
              aria-label=${this._composerExpanded ? 'Eingabe verkleinern' : 'Eingabe vergroessern'}
              aria-pressed=${this._composerExpanded ? 'true' : 'false'}
            >
              ${this._composerExpanded ? html`
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              ` : html`
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
                </svg>
              `}
            </button>
            ${this._sending ? html`
              <button
                class="cp-page-ceo-chats__send-btn cp-page-ceo-chats__send-btn--stop"
                @click=${() => void this._stop()}
                title="Antwort stoppen und Nachricht zurueckholen"
              >Stopp</button>
            ` : html`
              <button
                class="cp-page-ceo-chats__send-btn"
                @click=${() => void this._sendMessage()}
                ?disabled=${!canSend}
              >Senden</button>
            `}
          </div>
        </div>
      </div>
    `;
  }

  private _mobileBack(): void {
    this._mobileShowChat = false;
  }

  private _renderChatArea() {
    if (!this._activeThread) {
      return html`
        <div class="cp-page-ceo-chats__empty">
          <p>Waehle einen Thread aus oder starte ein neues Gespraech.</p>
        </div>
      `;
    }

    return html`
      <div class="cp-page-ceo-chats__chat-header">
        <button class="cp-page-ceo-chats__back-btn" @click=${this._mobileBack}>&larr;</button>
        <span class="cp-page-ceo-chats__chat-topic">${this._activeThread.topic}</span>
        <span class="cp-page-ceo-chats__chat-company">${this._activeCompanyName}</span>
      </div>

      <div class="cp-page-ceo-chats__messages">
        ${this._loadingThread ? html`<div class="cp-page-ceo-chats__loading">Laden...</div>` : nothing}
        ${this._messages.map(m => this._renderMessage(m))}
        ${this._sending ? html`
          <div class="cp-company-chat__message cp-company-chat__message--assistant">
            <div class="cp-company-chat__message-role">CEO</div>
            <div class="cp-company-chat__message-bubble">
              <div class="cp-company-chat__typing">...</div>
            </div>
          </div>
        ` : nothing}
      </div>

      ${this._error ? html`<div class="cp-page-ceo-chats__error">${this._error}</div>` : nothing}

      ${this._renderComposer(this._activeThread.id)}
    `;
  }

  override render() {
    return html`
      <div class="cp-page">
        <cp-page-subnav .tabs=${COMM_TABS} activePath="/ceo-chats"></cp-page-subnav>
        <h1 class="cp-page__title">CEO Chats</h1>
        <div class="cp-page-ceo-chats__wrap${this._mobileShowChat ? ' cp-page-ceo-chats__wrap--mobile-chat' : ''}">
          <aside class="cp-page-ceo-chats__sidebar">
            <div class="cp-page-ceo-chats__sidebar-header">Firmen</div>
            <div class="cp-page-ceo-chats__sidebar-list">
              ${this._renderSidebar()}
            </div>
          </aside>
          <main class="cp-page-ceo-chats__main${this._composerExpanded ? ' cp-page-ceo-chats__main--composer-expanded' : ''}">
            ${this._renderChatArea()}
          </main>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-ceo-chats': CpPageCeoChats;
  }
}
